import RequestHelper from "./request_helper"

// The image upload/crop/reset flow (Dropzone + Cropper.js) for a template editor mount, extracted
// out of templates_controller.js (R9) - its own Dropzone/Cropper instance state is independent of
// the controller's settings/font/text/color/visibility state, and nothing outside this file reads
// `this.cropper`. `element` is the controller's own mount (the same element the controller's
// `scoped()` helper is scoped to); `settings` is the SAME object the controller mutates via its
// `updateSettings()` - shared by reference, so resetImage's cache eviction below stays visible to
// the controller with no callback needed either way (the same shared-reference shape
// contact_drawer.js and order_calendar_drawer.js use for their own extracted state).
export default class TemplateImageUploader {
  constructor(element, settings) {
    this.element = element;
    this.settings = settings;
    this.cropper = undefined;
  }

  // Same jQuery-boundary helper as the controller's own `scoped()` (R8 justified skip) - this
  // module's queries feed Dropzone directly, which - like select2/spectrum/bootstrap-editable in
  // the controller - requires a jQuery-wrapped element.
  scoped(selector) {
    return $(this.element).find(selector);
  }

  logFailedCall(response) {
    console.warn(response);
  }

  bind() {
    this.scoped(".reset-image-message").on("click.templates", (event) => {
      const node = event.currentTarget.closest(".image-editor").dataset.node;

      if (this.settings[node] !== undefined) { delete this.settings[node]; }

      const options = RequestHelper.defaultDeleteOptions();
      const imagesUrl = this.scoped("#droppable").data("url");

      fetch(`${imagesUrl}/${node}`, options).
        then(response => response.json().then(data => ({ ok: response.ok, data }))).
        then(({ ok, data }) => {
          if (!ok) {
            const message = data.message === undefined ?
              "An internal error occurred. Please try again later." :
              data.message;
            window.ltAlert({ message: message, type: "toast-error" });
            return;
          }

          const $image = this.scoped(`#${node}-image`);
          $image.attr("src", $image.data("defaulturl"));
          $image.attr("width", $image.data("defaultwidth"));
          $image.attr("height", $image.data("defaultheight"));
          $image.removeAttr("style");
          $image.removeClass("uploaded");

          this.scoped("button#cancel-upload").click();
          window.ltAlert({ message: data.message, duration: 2500 });

          const editor = this.scoped(`#image-editor-${node}`);
          const defaultEditorImage = editor.data("defaulturl");
          editor.find(".editor-thumbnail").attr("src", defaultEditorImage);
          editor.find(".upload-image-message").removeClass("hidden");
          editor.find(".reset-image-message").addClass("hidden");
        }).
        catch(error => this.logFailedCall(error));
    });

    this.scoped("#select-image").on("hide.bs.modal.templates", (event) => {
      this.scoped("#droppable").removeClass("hidden");
      this.scoped("#image-upload-complete").addClass("hidden");

      if (event.target.id === "select-image") {
        this.disableDropzone();
      }
    });

    this.scoped("button#upload-image").on("click.templates", () => {
      this.scoped("#droppable")[0].dropzone.processQueue();
    });

    this.scoped(".re-upload").on("click.templates", (event) => {
      event.preventDefault();
      const node = this.scoped("#droppable").data("node");
      this.disableDropzone();
      this.enableDropzoneForNode(node);
      event.currentTarget.classList.add("hidden");
    });

    this.scoped(".trigger-image-upload").on("click.templates", (event) => {
      if (event.target.classList.contains("reset-image-message")) { return; }
      if (event.currentTarget.classList.contains("disabled")) { return; }

      const node = event.currentTarget.dataset.node;
      this.enableDropzoneForNode(node);

      const $selectImage = this.scoped("#select-image");
      $selectImage.modal({ backdrop: "static", keyboard: false });
      $selectImage.modal("show");
    });
  }

  unbind() {
    this.scoped(".reset-image-message").off(".templates");
    this.scoped("#select-image").off(".templates");
    this.scoped("button#upload-image").off(".templates");
    this.scoped(".re-upload").off(".templates");
    this.scoped(".trigger-image-upload").off(".templates");
  }

  destroy() {
    if (this.cropper !== undefined) {
      this.cropper.destroy();
      this.cropper = undefined;
    }

    const droppable = this.scoped("#droppable");
    if (droppable.length && droppable[0].dropzone !== undefined) {
      droppable[0].dropzone.destroy();
      droppable[0].dropzone = undefined;
    }
  }

  enableCropperForNode(node, url) {
    if (this.cropper !== undefined) {
      this.cropper.replace(url);
    } else {
      const $cropper = this.scoped("#cropper");
      const $nodeImage = this.scoped(`#${node}-image`);

      $cropper.attr("src", url);
      const size = { width: $nodeImage.data("width"), height: $nodeImage.data("height") };

      this.cropper = new Cropper($cropper[0], { aspectRatio: size.width / size.height, viewMode: 0 });
    }

    this.scoped("#cropper-wrapper").removeClass("gif-preview");
    this.scoped(".gif-upload-hint").addClass("hidden");
    this.scoped("#droppable .dz-preview").removeClass("hidden");
    this.scoped("#droppable").removeClass("gif-preview-active");
    this.scoped("#cropper-wrapper").show();
  }

  showGifPreview(url) {
    if (this.cropper !== undefined) {
      this.cropper.destroy();
      this.cropper = undefined;
    }

    const $cropper = this.scoped("#cropper");
    $cropper.attr("src", url);
    this.scoped("#cropper-wrapper").addClass("gif-preview");
    this.scoped(".gif-upload-hint").removeClass("hidden");
    this.scoped("#cropper-wrapper").show();
    this.scoped("#droppable .dz-preview").addClass("hidden");
    this.scoped("#droppable").addClass("gif-preview-active");
  }

  disableDropzone() {
    this.scoped("#cropper-wrapper").hide();
    this.scoped("#cropper-wrapper").removeClass("gif-preview");
    this.scoped(".gif-upload-hint").addClass("hidden");
    this.scoped("#droppable .dz-preview").removeClass("hidden");
    this.scoped("#droppable").removeClass("gif-preview-active");
    this.scoped(".removeable").show();
    this.scoped("#image-upload-complete").addClass("hidden");
    this.scoped("#image-upload-bar").addClass("hidden");

    if (this.cropper !== undefined) {
      this.cropper.destroy();
      this.cropper = undefined;
    }

    this.scoped("button#upload-image").addClass("btn-disabled");

    const droppable = this.scoped("#droppable")[0];
    droppable.dropzone.removeAllFiles();
    droppable.dropzone.disable();

    if (droppable.dropzone !== undefined) { droppable.dropzone = undefined; }
  }

  enableDropzoneForNode(node) {
    const $droppable = this.scoped("#droppable");
    $droppable.data("node", node);

    if ($droppable[0].dropzone === undefined) {
      const uploader = this;

      $droppable.dropzone({
        url: $droppable.data("url"),
        acceptedFiles: "image/*",
        autoProcessQueue: false,
        maxFilesize: 1,
        maxFiles: 1,
        thumbnailWidth: 100,
        thumbnailHeight: 100,
        clickable: ".removeable",
        resizeMethod: "contain",
        paramName: `settings[${node}]`,
        previewTemplate:
          `<div class="dz-preview dz-file-preview">
            <div class="dz-details">
              <div class="dz-filename"><p class="text-subheader" data-dz-name></p></div>
              <div class="dz-size">
                <i class="icon-lasso-info"></i>
                <p>File size: <span data-dz-size></span></p>
              </div>
              <div class="dz-error-message">
                <i class="icon-lasso-warning-stroke-triangle"></i>
                <p data-dz-errormessage></p>
              </div>
            </div>
            <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
          </div>`,
        headers: {
          "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
        },
        maxfilesexceeded: function(files) {
          this.removeAllFiles();
          this.addFile(files);
        },
        init: function() {
          const dz = this;

          dz.on("addedfile", () => {
            uploader.scoped(".dz-clickable").hide();
            uploader.scoped(".droppable-details-box").css("display", "flex");
            uploader.scoped("#cropper-wrapper").show();
          });

          dz.on("thumbnail", (file) => {
            if (file.status !== "error") {
              uploader.scoped("button#upload-image").removeClass("btn-disabled");
            }

            uploader.scoped(".re-upload").removeClass("hidden");

            if (file.type === "image/gif") {
              uploader.showGifPreview(file.dataURL);
            } else {
              uploader.enableCropperForNode(node, file.dataURL);
            }
          });

          dz.on("sending", () => {
            uploader.scoped("button#upload-image").addClass("btn-disabled");
            uploader.scoped("#droppable").addClass("hidden");
            uploader.scoped("#image-upload-bar").removeClass("hidden");
            uploader.scoped(".re-upload").addClass("hidden");
            uploader.scoped("#cancel-upload").addClass("hidden");
            uploader.scoped(".close-modal-button").addClass("hidden");
          });

          dz.on("uploadprogress", (file, progress) => {
            uploader.scoped("#image-upload-bar .progress-bar").css("width", progress + "%");
          });

          dz.on("success", (file, response) => {
            uploader.scoped("#image-upload-bar .progress-bar").css("width", "0%");
            uploader.scoped("#image-upload-complete").removeClass("hidden");
            uploader.scoped("#image-upload-bar").addClass("hidden");
            uploader.scoped("#cancel-upload").removeClass("hidden");
            uploader.scoped(".close-modal-button").removeClass("hidden");

            const $image = uploader.scoped(`#${node}-image`);
            $image.attr("src", response.url);
            $image.addClass("uploaded");
            const isGif = response.url && response.url.toLowerCase().indexOf(".gif") !== -1;
            if (isGif) {
              $image.attr("height", $image.data("height"));
              $image.removeAttr("width");
              $image.css("height", $image.data("height") + "px");
              $image.css("width", "auto");
            } else {
              $image.removeAttr("style");
              $image.attr("width", $image.data("width"));
              $image.attr("height", $image.data("height"));
            }

            const editor = uploader.scoped(`#image-editor-${node}`);
            const $editorThumbnail = editor.find(".editor-thumbnail");
            $editorThumbnail.attr("src", response.url);
            if (isGif) {
              $editorThumbnail.attr("height", $image.data("height"));
              $editorThumbnail.removeAttr("width");
              $editorThumbnail.css("height", $image.data("height") + "px");
              $editorThumbnail.css("width", "auto");
            } else {
              $editorThumbnail.removeAttr("style");
              $editorThumbnail.attr("width", $image.data("width"));
              $editorThumbnail.attr("height", $image.data("height"));
            }
            editor.find(".upload-image-message").addClass("hidden");
            editor.find(".reset-image-message").removeClass("hidden");

            setTimeout(() => {
              uploader.scoped("button#cancel-upload").click();
              window.ltAlert({ message: response.message, type: "toast-success", duration: 2500 });
            }, 1000);
          });

          dz.on("complete", (file) => {
            if (!file.accepted || (file.status !== "error")) { return; }

            uploader.scoped("#image-upload-bar .progress-bar").css("width", "0%");
            window.ltAlert({ message: "There was an error uploading your image", type: "toast-danger", duration: 2500 });
            uploader.scoped("#image-upload-bar").addClass("hidden");
            uploader.scoped("#cancel-upload").removeClass("hidden");
            uploader.scoped(".close-modal-button").removeClass("hidden");

            const $droppable = uploader.scoped("#droppable");
            $droppable.removeClass("hidden");

            uploader.disableDropzone();
            uploader.enableDropzoneForNode($droppable.data("node"));
          });
        },
        transformFile: function(file, done) {
          if (file.type === "image/gif") {
            done(file);
            return;
          }
          const canvas = uploader.cropper.getCroppedCanvas();
          canvas.toBlob(function(blob) {
            done(blob);
          });
        }
      });
    } else {
      $droppable[0].dropzone.enable();
    }
  }
}
